import { useGetUserFares } from '../../services/userFare';
import { useUser } from '../../hooks/useUser';
import { BranchItem } from './components/BranchItem/BranchItem';
import { useBranch } from '../../hooks/useBranch';
import { type IBranch } from '../../types/branch';
import { useGetBranches } from '../../services/branch';
import { BRANCH_KEY, LAST_ACCESSED_BRANCH_KEY } from '../../constants/storageKeys';
import { useEffect, useState } from 'react';
import { LoadingScreen } from '../LoadingScreen';
import { useAuth } from '../../hooks/useAuth';
import { IconLogout } from '@tabler/icons-react';

const BranchSelectionPage = () => {
  const { userFares, loading } = useGetUserFares();
  const { user, isAdmin, isSuperAdmin } = useUser();
  const { logout } = useAuth();
  const { setBranch } = useBranch();
  const [loadingFromStorage, setLoadingFromStorage] = useState(true);
  const [lastBranch, setLastBranch] = useState<IBranch | null>(null);

  const { branches, loading: loadingBranches } = useGetBranches();

  useEffect(() => {
    const branchStored = localStorage.getItem(BRANCH_KEY);
    const lastBranchStored = localStorage.getItem(LAST_ACCESSED_BRANCH_KEY);

    if (branchStored) {
      setBranch(JSON.parse(branchStored));
      setLoadingFromStorage(false);
    }

    if (lastBranchStored) {
      try {
        setLastBranch(JSON.parse(lastBranchStored));
      } catch (e) {
        console.error('Error parsing last branch', e);
      }
    }

    setLoadingFromStorage(false);
  }, []);

  const onSelectBranch = (branch: IBranch) => {
    localStorage.setItem(BRANCH_KEY, JSON.stringify(branch));
    localStorage.setItem(LAST_ACCESSED_BRANCH_KEY, JSON.stringify(branch));

    setBranch(branch);
  };

  if (loading || loadingBranches || loadingFromStorage) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-[#121824] transition-colors p-6 md:p-12">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 transition-colors">
            MIS OPOSICIONES
          </h2>

          {/* User Info Card */}
          <div className="relative overflow-hidden rounded-3xl bg-white/40 dark:bg-white/5 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl transition-all duration-500 group">
            {/* Animated Background Mesh */}
            <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl opacity-50 group-hover:opacity-70 transition-opacity duration-700 animate-pulse"></div>
            <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl opacity-50 group-hover:opacity-70 transition-opacity duration-700 animate-pulse delay-700"></div>

            <div className="relative p-8 flex flex-col sm:flex-row items-center gap-8 z-10">
              {/* Avatar Container */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500 via-purple-500 to-pink-500 rounded-full blur-lg opacity-70 animate-spin-slow"></div>
                <div className="relative p-[3px] rounded-full bg-gradient-to-tr from-indigo-500 via-purple-500 to-pink-500">
                  <img
                    src={user?.avatar}
                    alt={user?.name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-white dark:border-[#1a1f2e] shadow-inner"
                  />
                </div>
                {/* Status Indicator */}
                <div className="absolute bottom-1 right-1 w-6 h-6 bg-green-500 border-4 border-white dark:border-[#1a1f2e] rounded-full shadow-md"></div>
              </div>

              {/* Text Content */}
              <div className="text-center sm:text-left flex-1">
                <p className="text-indigo-600 dark:text-indigo-400 font-medium tracking-wide uppercase text-sm mb-1">
                  Bienvenido de nuevo
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
                  {user?.name}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 text-lg max-w-lg mb-6">
                  ¿Qué te gustaría estudiar hoy? Selecciona una de tus oposiciones activas para continuar.
                </p>

                <button
                  onClick={logout}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-white/50 dark:bg-white/10 hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-700 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400 transition-all duration-300 border border-white/20 dark:border-white/5 group/logout"
                >
                  <IconLogout size={18} className="transform group-hover/logout:-translate-x-1 transition-transform" />
                  <span className="font-medium">Cerrar sesión</span>
                </button>
              </div>

              {/* Decorative Icon/Action (Optional visual balance) */}
              <div className="hidden lg:block opacity-20 transform rotate-12 group-hover:rotate-0 transition-transform duration-700">
                <svg className="w-32 h-32 text-indigo-900 dark:text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L2 7l10 5 10-5-10-5zm0 9l2.5-1.25L12 8.5l-2.5 1.25L12 11zm0 2.5l-5-2.5-5 2.5L12 22l10-8.5-5-2.5-5 2.5z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Continue Studying Section */}
        {lastBranch && (
          <div className="mb-12 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Continuar estudiando
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <BranchItem branch={lastBranch} onClick={onSelectBranch} />
            </div>
          </div>
        )}

        {/* Branches Grid */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 transition-colors flex items-center gap-3">
            <span className="w-2 h-8 bg-indigo-500 rounded-full"></span>
            Todas tus oposiciones
          </h2>

          {(isAdmin || isSuperAdmin) && branches && branches.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {branches.map((branch) => (
                <BranchItem key={branch._id} branch={branch} onClick={onSelectBranch} />
              ))}
            </div>
          )}

          {!(isAdmin || isSuperAdmin) && userFares.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {userFares.map((fare) => (
                <BranchItem key={fare._id} branch={fare.branch} onClick={onSelectBranch} />
              ))}
            </div>
          )}

          {!(isAdmin || isSuperAdmin) && userFares.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="bg-white dark:bg-white/5 backdrop-blur-sm rounded-2xl p-12 shadow-lg border border-gray-200 dark:border-white/10 transition-colors text-center max-w-md">
                <svg
                  className="w-20 h-20 mx-auto mb-6 text-gray-400 dark:text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 transition-colors">
                  No tienes suscripciones
                </h3>
                <p className="text-gray-600 dark:text-gray-400 transition-colors">
                  No tienes ninguna suscripción activa en este momento
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { BranchSelectionPage };
